import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import {
  BathRoomIconRent,
  BedIconRent,
  FavoriteIconRent,
  GuestsIcon,
  HeartIconRent,
  NUSDIcon,
  PinIcon,
  UnHeartIconRent,
} from "../../AssetComponents/Images";
import { PurpleButton } from "../Buttons/PurpleButton";
import { BlackButton } from "../Buttons/BlackButton";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, memo, useMemo, useCallback, useRef } from "react";
import { api } from "../functions/Api";
import { setConnectModal } from "../../ReduxSlices/ModalSlice";
// import ConfettiExplosion from "react-confetti-explosion";
// 🚀 OPTIMIZED: Removed unused Mainnet import
import imageOptimizationService from "../../Services/ImageOptimizationService";
import { PropertyImageWithFallback } from "../Images/PropertyImageWithFallback";
// 🚀 OPTIMIZED: Removed unused imports - no longer making individual RPC calls
import IcalModal from "../Modal/IcalModal";
import { normalizePricePerDay } from "../../utils/propertyValidation";

const renderCustomPrevArrow = (onClickHandler, hasPrev, label) => {
  return (
    <div
      onClick={onClickHandler}
      disabled={!hasPrev}
      className="custom-arrow custom-prev-arrow p-1 rounded-full bg-[#ffffff80] hover:bg-[#ffffff]"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.75 9H14.25"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.5 12.75L3.75 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.5 5.25L3.75 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

// Custom render function for next arrow
const renderCustomNextArrow = (onClickHandler, hasNext, label) => {
  return (
    <div
      onClick={onClickHandler}
      disabled={!hasNext}
      className="custom-arrow custom-next-arrow p-1 rounded-full bg-[#ffffff80] hover:bg-[#ffffff]"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.25 9H3.75"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 12.75L14.25 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 5.25L14.25 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

// Custom render function for indicators - limit to max 5 dots
const renderCustomIndicator = (onClickHandler, isSelected, index, label) => {
  // Only render indicators for the first 5 images (index 0-4)
  if (index >= 5) {
    return null;
  }
  
  return (
    <li
      className={`control-dot ${isSelected ? 'selected' : ''}`}
      onClick={onClickHandler}
      aria-label={label}
      role="button"
      tabIndex={0}
    />
  );
};

export const RentalItem = memo(({
  token_id,
  tokenData,
  onClick,
  imageHeight = 180,
  priceHide,
  showArrow = true,
  onlyImages,
  // status = 0,
  rentingPeriod = [],
  traveler,
  borderNone,
  confetti = true,
  hideWishlist = false,
  forceWishlisted = false,
  cancelled: cancelledProp = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [avgRate, setAvgRate] = useState();
  const token = useSelector((state) => state.nft.nfts[token_id]);
  const account = useSelector((state) => state.auth.account);
  const [wishlisted, setWishlisted] = useState(forceWishlisted);
  const [status, setStatus] = useState();
  const diff = useSelector((state) => state.time.diffToUTC);
  const dispatch = useDispatch();
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [showIcalModal, setShowIcalModal] = useState(false);
  const isOwner = (token || tokenData)?.access?.owner === account;

  // Preload images when component mounts
  useEffect(() => {
    if (token || tokenData) {
      imageOptimizationService.preloadPropertyImages(token || tokenData);
    }
  }, [token, tokenData]);

  // Memoized price calculation - format to avoid scientific notation
  // For completed/cancelled trips, show the actual deposit amount paid
  const pricePerDay = useMemo(() => {
    // First, try to find the matching reservation to get deposit_amount
    let depositAmount = null;
    let isCancelledReservation = false;
    if (token && rentingPeriod.length === 2 && traveler) {
      for (let i = 0; i < token.rentals?.rentals?.length; i++) {
        const reservation = token.rentals.rentals[i];
        if (
          reservation.renting_period[0] === rentingPeriod[0] &&
          reservation.renting_period[1] === rentingPeriod[1] &&
          reservation.address === traveler
        ) {
          // Extract deposit_amount if available
          if (reservation.deposit_amount) {
            const usdcDecimals = Number(process.env.REACT_APP_USDC_DECIMALS) || 6;
            depositAmount = Number(reservation.deposit_amount) / (10 ** usdcDecimals);
          }
          isCancelledReservation = !!reservation.cancelled;
          break;
        }
      }
    }

    // For completed or cancelled trips, show the deposit amount instead of price_per_day
    if (depositAmount !== null && (status === 3 || status === 2)) {
      // Format deposit amount to avoid scientific notation
      const price = depositAmount;
      if (price === 0) return 0;
      if (price < 0.000001) {
        const formatted = price.toFixed(10);
        return formatted.replace(/\.?0+$/, '');
      } else if (price < 0.01) {
        const formatted = price.toFixed(6);
        return formatted.replace(/\.?0+$/, '');
      } else if (price < 1) {
        const formatted = price.toFixed(4);
        return formatted.replace(/\.?0+$/, '');
      } else {
        return price.toLocaleString('en-US', { 
          minimumFractionDigits: 0, 
          maximumFractionDigits: 2 
        });
      }
    }

    // If reservation is cancelled and deposit is missing/zero, fall back to total price (nights * price_per_day)
    if ((status === 2 || isCancelledReservation) && (!depositAmount || depositAmount === 0)) {
      const shortPrice = normalizePricePerDay((token || tokenData)?.short?.price_per_day || 0);
      const nights = Math.max(1, Math.round((rentingPeriod[1] - rentingPeriod[0]) / 86400));
      const total = Number(shortPrice) * nights;
      if (total === 0) return 0;
      if (total < 0.000001) {
        const formatted = total.toFixed(10);
        return formatted.replace(/\.?0+$/, '');
      } else if (total < 0.01) {
        const formatted = total.toFixed(6);
        return formatted.replace(/\.?0+$/, '');
      } else if (total < 1) {
        const formatted = total.toFixed(4);
        return formatted.replace(/\.?0+$/, '');
      } else {
        return total.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      }
    }

    // Otherwise, show the property's price_per_day
    // Normalize price to handle both raw format (70000000) and converted format (70)
    let price = normalizePricePerDay((token || tokenData)?.short?.price_per_day || 0);
    
    if (price === 0) return 0;
    
    // Format to avoid scientific notation
    if (price < 0.000001) {
      const formatted = price.toFixed(10);
      return formatted.replace(/\.?0+$/, '');
    } else if (price < 0.01) {
      const formatted = price.toFixed(6);
      return formatted.replace(/\.?0+$/, '');
    } else if (price < 1) {
      const formatted = price.toFixed(4);
      return formatted.replace(/\.?0+$/, '');
    } else {
      return price.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      });
    }
  }, [token, tokenData, rentingPeriod, traveler, status]);

  // Memoized property data
  const propertyData = useMemo(() => {
    return token || tokenData;
  }, [token, tokenData]);

  // 🚀 OPTIMIZED: Use tokenData prop if token not in Redux yet (from getMyTrips batch dispatch)
  // Don't make individual RPC calls - data should already be available
  const decideStatus = useCallback(() => {
    console.log("🔄 [RENTAL_ITEM] decideStatus called for:", {
      token_id,
      traveler,
      rentingPeriod,
      hasToken: !!token,
      hasTokenData: !!tokenData,
    });
    
    const currentToken = token || tokenData;
    if (cancelledProp) {
      setStatus(2);
      return;
    }
    if (!currentToken || !currentToken.rentals?.rentals || !Array.isArray(currentToken.rentals.rentals)) {
      console.log("⏭️ [RENTAL_ITEM] No token or rentals data, setting status to null");
      setStatus(null);
      return;
    }

    if (rentingPeriod.length !== 2 || !traveler) {
      console.log("⏭️ [RENTAL_ITEM] Invalid rentingPeriod or traveler, setting status to null");
      setStatus(null);
      return;
    }

    console.log(`🔍 [RENTAL_ITEM] Searching through ${currentToken.rentals.rentals.length} reservations...`);
    for (let i = 0; i < currentToken.rentals.rentals.length; i++) {
      const reservation = currentToken.rentals.rentals[i];
      const isMatch = 
        reservation.renting_period?.[0] === rentingPeriod[0] &&
        reservation.renting_period?.[1] === rentingPeriod[1] &&
        reservation.address === traveler;
      
      if (isMatch) {
        console.log(`✅ [RENTAL_ITEM] Found matching reservation:`, {
          approved: reservation.approved,
          cancelled: reservation.cancelled,
          address: reservation.address,
          renting_period: reservation.renting_period,
        });
        
        if (reservation.cancelled) {
          console.log("📊 [RENTAL_ITEM] Setting status to 2 (Cancelled)");
          setStatus(2);
          return;
        } else if (reservation.approved) {
          const currentTime = new Date().getTime() / 1000;
          const endTime = rentingPeriod[1] - diff;
          if (currentTime < endTime) {
            console.log("📊 [RENTAL_ITEM] Setting status to 0 (Approved and upcoming)");
            setStatus(0);
            return;
          } else {
            console.log("📊 [RENTAL_ITEM] Setting status to 3 (Approved and completed)");
            setStatus(3);
            return;
          }
        } else if (!reservation.approved) {
          const currentTime = new Date().getTime() / 1000;
          const startTime = rentingPeriod[0] - diff;
          if (currentTime < startTime) {
            console.log("📊 [RENTAL_ITEM] Setting status to 1 (Pending approval - before start)");
            setStatus(1);
            return;
          } else {
            console.log("📊 [RENTAL_ITEM] Setting status to 4 (Pending approval - after start)");
            setStatus(4);
            return;
          }
        }
      }
    }
    console.log("⏭️ [RENTAL_ITEM] No matching reservation found, setting status to null");
    setStatus(null);
  }, [rentingPeriod, traveler, token, tokenData, diff, token_id, cancelledProp]);

  // Payment confirmed = reservation has txHash set by backend after payment
  const paymentConfirmed = useMemo(() => {
    const currentToken = token || tokenData;
    if (!currentToken?.rentals?.rentals || rentingPeriod.length !== 2 || !traveler) return false;
    const reservation = currentToken.rentals.rentals.find(
      (r) =>
        r.renting_period?.[0] === rentingPeriod[0] &&
        r.renting_period?.[1] === rentingPeriod[1] &&
        r.address === traveler
    );
    return !!(reservation?.txHash && String(reservation.txHash).trim());
  }, [token, tokenData, rentingPeriod, traveler]);

  useEffect(() => {
    decideStatus();
  }, [decideStatus]);

  // 🚀 FIX: Force status recalculation when token rentals data changes
  useEffect(() => {
    console.log("🔄 [RENTAL_ITEM] Token rentals data changed, checking reservation...");
    if (token?.rentals?.rentals) {
      // Check if our reservation exists and has been updated
      const reservation = token.rentals.rentals.find(
        (r) =>
          r.renting_period?.[0] === rentingPeriod[0] &&
          r.renting_period?.[1] === rentingPeriod[1] &&
          r.address === traveler
      );
      console.log("🔍 [RENTAL_ITEM] Reservation found in token data:", {
        found: !!reservation,
        approved: reservation?.approved,
        cancelled: reservation?.cancelled,
      });
      if (reservation) {
        console.log("🔄 [RENTAL_ITEM] Reservation found, recalculating status...");
        // Force status recalculation when reservation data changes
        decideStatus();
      }
    }
  }, [token?.rentals?.rentals, decideStatus, rentingPeriod, traveler]);

  // 🚀 FIX: Listen for force refresh events
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log("🔄 Force refresh event received, recalculating status...");
      decideStatus();
    };

    window.addEventListener('reservation:forceRefresh', handleForceRefresh);
    return () => {
      window.removeEventListener('reservation:forceRefresh', handleForceRefresh);
    };
  }, [decideStatus]);

  // 🚀 FIX: Listen for reservation approval events to update status immediately
  useEffect(() => {
    const handleReservationApproval = (event) => {
      const detail = event.detail || {};
      console.log("📥 [RENTAL_ITEM] Reservation approval event received:", {
        eventType: event.type,
        detail,
        currentTokenId: token_id,
        currentTraveler: traveler,
        currentPeriod: rentingPeriod,
      });
      
      // Check if this approval is for the current reservation
      const isMatchingReservation = 
        detail.token_id === token_id &&
        detail.traveler === traveler &&
        Array.isArray(detail.renting_period) &&
        detail.renting_period[0] === rentingPeriod[0] &&
        detail.renting_period[1] === rentingPeriod[1];
      
      console.log("🔍 [RENTAL_ITEM] Is matching reservation?", isMatchingReservation);
      
      if (isMatchingReservation) {
        console.log("🔄 [RENTAL_ITEM] Reservation approved event matches, recalculating status...");
        // Force immediate status update - check token directly
        const currentToken = token || tokenData;
        console.log("🔍 [RENTAL_ITEM] Current token data:", {
          hasToken: !!currentToken,
          hasRentals: !!currentToken?.rentals?.rentals,
          rentalsCount: currentToken?.rentals?.rentals?.length || 0,
        });
        
        if (currentToken?.rentals?.rentals) {
          const reservation = currentToken.rentals.rentals.find(
            (r) =>
              r.renting_period?.[0] === rentingPeriod[0] &&
              r.renting_period?.[1] === rentingPeriod[1] &&
              r.address === traveler
          );
          
          console.log("🔍 [RENTAL_ITEM] Found reservation:", {
            found: !!reservation,
            approved: reservation?.approved,
            cancelled: reservation?.cancelled,
          });
          
          if (reservation?.approved) {
            console.log("✅ [RENTAL_ITEM] Reservation is approved, updating status...");
            // Force status recalculation
            setTimeout(() => {
              console.log("🔄 [RENTAL_ITEM] Calling decideStatus()...");
              decideStatus();
            }, 100);
            // Also force a re-render by updating status directly if needed
            const currentTime = new Date().getTime() / 1000;
            const endTime = rentingPeriod[1] - diff;
            if (currentTime < endTime) {
              console.log("📊 [RENTAL_ITEM] Setting status to 0 (Approved and upcoming)");
              setStatus(0); // Approved and upcoming
            } else {
              console.log("📊 [RENTAL_ITEM] Setting status to 3 (Approved and completed)");
              setStatus(3); // Approved and completed
            }
          }
        }
      }
    };

    // Listen for reservation approval events
    window.addEventListener('reservation:approved', handleReservationApproval);
    window.addEventListener('reservationStatusUpdate', handleReservationApproval);

    return () => {
      window.removeEventListener('reservation:approved', handleReservationApproval);
      window.removeEventListener('reservationStatusUpdate', handleReservationApproval);
    };
  }, [token_id, traveler, rentingPeriod, decideStatus, token, tokenData, diff]);

  // 🚀 OPTIMIZED: Memoize getReview to prevent duplicate calls
  const getReview = useCallback(async () => {
    if (!token_id) return;
    try {
      const res = await api("review/get", {
        token_id: token_id,
      });
      if (!res || res.length === 0) return;
      let avgRate_ = 0;
      for (let i = 0; i < res.length; i++) {
        // Cap individual rates at 5 to prevent invalid data
        const reviewRate = Math.min(Math.max(res[i].rate || 0, 0), 5);
        avgRate_ += reviewRate;
      }
      const calculatedAvg = avgRate_ / res.length;
      // Cap the average rating at 5
      const cappedAvg = Math.min(calculatedAvg, 5);
      setAvgRate(cappedAvg.toFixed(1));
    } catch (error) {
      console.error("Failed to get reviews:", error);
    }
  }, [token_id]);

  const addToWistlist = async () => {
    if (!account) {
      dispatch(setConnectModal(true));
      return;
    }
    
    // Show loading state
    setIsWishlistLoading(true);
    
    try {
      // Your backend already handles toggling in one endpoint
      const res = await api("user/addToWishlist", {
        account: account,
        token_id: token_id,
      });
      
      if (res !== undefined) {
        // Update state based on response (true = in wishlist, false = not in wishlist)
        setWishlisted(res);
        
        // 🚀 FIX: Dispatch multiple events to ensure all components refresh
        // Event for dashboard refresh
        window.dispatchEvent(new CustomEvent('wishlistUpdated', {
          detail: { token_id, inWishlist: res }
        }));
        
        // Event to force all RentalItems to re-check their wishlist status
        // Use a small delay to ensure backend has processed
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('wishlistStatusChanged', {
            detail: { token_id, inWishlist: res }
          }));
        }, 100);
      } else {
        // If response is undefined, force re-check to get correct state
        setTimeout(() => {
          checkWistlist(true);
        }, 300);
      }
    } catch (error) {
      console.error("Wishlist operation failed:", error);
      // On error, force re-check to get correct state
      if (account && token_id) {
        setTimeout(() => {
          checkWistlist();
        }, 500);
      }
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const checkWistlist = async (forceRefresh = false) => {
    if (!account || !token_id) return;
    try {
      // Always get fresh data - don't rely on cached responses
      const res = await api("user/checkWishlist", {
        account: account,
        token_id: token_id,
      });
      setWishlisted(!!res); // Ensure boolean value
    } catch (error) {
      console.error("Failed to check wishlist status:", error);
      // Don't update state on error - keep current state
    }
  };

  // Update wishlisted state when forceWishlisted prop changes
  useEffect(() => {
    if (forceWishlisted) {
      setWishlisted(true);
    }
  }, [forceWishlisted]);

  // Check wishlist on mount and when account changes
  useEffect(() => {
    if (token_id) {
      getReview();
      if (account && !forceWishlisted) {
        // Only check wishlist if not forced (items in wishlist section are already wishlisted)
        checkWistlist();
      }
    }
  }, [token_id, account]);

  // 🚀 FIX: Listen for wishlist status changes and re-check this item's status
  useEffect(() => {
    if (!account || !token_id || forceWishlisted) return;
    
    const handleWishlistStatusChange = (event) => {
      // Re-check this item's wishlist status after any wishlist operation
      // This ensures all items on the page stay in sync
      setTimeout(() => {
        checkWistlist(true); // Force refresh
      }, 300);
    };

    // Also listen for page visibility changes to refresh when tab becomes active
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          checkWistlist(true); // Force refresh when tab becomes visible
        }, 100);
      }
    };

    window.addEventListener('wishlistStatusChanged', handleWishlistStatusChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('wishlistStatusChanged', handleWishlistStatusChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [account, token_id, forceWishlisted]);

  return (
    <div
      className={
        !borderNone
          ? "hover:bg-[#AAAAAA30] relative group bg-white w-full max-w-[300px] min-w-[240px] rounded-[8px] h-max p-[10px] shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.8),2px_2px_6px_0px_rgba(187,195,206,0.6)] opacity-0 animate-fadeIn"
          : "hover:bg-[#AAAAAA30] relative group bg-white w-full max-w-[300px] min-w-[240px] rounded-t-[8px] h-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.8),2px_2px_6px_0px_rgba(187,195,206,0.6)] opacity-0 animate-fadeIn"
      }
      style={{ animationFillMode: 'forwards' }}
    >
      {borderNone ? (
        <div className="w-full h-full rounded-t-[10px] bg-gray-200 flex items-center justify-center">
          <PropertyImageWithFallback
            src={(token || tokenData)?.metaData?.images?.[0]}
            alt="Property image"
            className="w-full h-full rounded-t-[10px] object-cover"
            priority={true}
            lazy={false}
            width={800}
            height={imageHeight}
            quality={90}
            progressive={true}
          />
        </div>
      ) : (
        <Carousel
          showStatus={false}
          showArrows={showArrow}
          showThumbs={false}
          renderArrowPrev={renderCustomPrevArrow}
          renderArrowNext={renderCustomNextArrow}
          renderIndicator={renderCustomIndicator}
        >
          {(token || tokenData)?.metaData?.images && (token || tokenData)?.metaData.images.length > 0 ? 
            (token || tokenData)?.metaData.images.map((image, i) => {
              return (
                <div
                  className={`h-[${imageHeight}px] rounded-[10px] relative`}
                  key={i}
                >
                  <div className="w-full h-full absolute group-hover:bg-[#00000030] top-0 left-0 rounded-[10px]"></div>

                  <PropertyImageWithFallback
                    src={image}
                    alt={`Property image ${i + 1}`}
                    className={`h-[${imageHeight}px] rounded-[10px] w-full object-cover`}
                    priority={i === 0} // First image gets priority loading
                    lazy={i > 0} // Lazy load subsequent images
                    width={800}
                    height={imageHeight}
                    quality={i === 0 ? 90 : 80} // Higher quality for first image
                    progressive={true} // Enable progressive loading
                  />
                </div>
              );
            }) : (
              <div className={`h-[${imageHeight}px] rounded-[10px] relative bg-gray-200 flex items-center justify-center`}>
                <div className="text-gray-500 text-center">
                  <div className="text-4xl mb-2">🏠</div>
                  <div className="text-sm">No images available</div>
                </div>
              </div>
            )
          }
        </Carousel>
      )}
      {/* {wishlisted && confetti && (
        <ConfettiExplosion
          className="top-6 right-6 absolute"
          width={400}
          particleCount={30}
          force={0.1}
          // height={}
          particleSize={5}
          duration={2200}
          colors={["#5b1dee", "#4E59FA", "#24DBFE"]}
        />
      )} */}
   {!hideWishlist && (
     <div
       onClick={(e) => {
         e.stopPropagation();
         if (!isWishlistLoading) {
           addToWistlist();
         }
       }}
     >
       {isWishlistLoading ? (
         // Simple spinner during loading
         <div className={!borderNone ? "absolute right-[20px] top-[20px]" : "absolute right-[10px] top-[10px]"}>
           <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full"></div>
         </div>
       ) : (wishlisted ? (
         <HeartIconRent 
           tooltip="removeFromWishlist"
           className={!borderNone ? "absolute right-[20px] top-[20px] cursor-pointer" : "absolute right-[10px] top-[10px] cursor-pointer"}
         />
       ) : (
         <UnHeartIconRent
           tooltip="addToWishlist" 
           className={!borderNone ? "absolute right-[20px] top-[20px] cursor-pointer" : "absolute right-[10px] top-[10px] cursor-pointer"}
         />
       ))}
     </div>
   )}
      <div
        className={
          !borderNone
            ? "absolute left-[20px] top-[20px]"
            : "absolute left-[10px] top-[10px]"
        }
      >
        {status === 0 && (
          <div className="p-1 text-[#38A569] gap-1 bg-white rounded-full border-[1px] border-[#38A569] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.54167 9.91667L2.625 7"
                  stroke="#38A569"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.3763 4.08331L5.54297 9.91665"
                  stroke="#38A569"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div className="text-[15px] font-light">Approved</div>
          </div>
        )}

        {status === 1 && (
          <div className="p-1 text-[#F8BC30] gap-1 bg-white rounded-full border-[1px] border-[#F8BC30] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.0013 7.34167V8.75001"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M3.34323 6.65833L2.35156 7.64999"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.65833 5.00002H1.25"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.35156 2.34998L3.34323 3.34164"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M5.0013 2.5V1.25"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.98828 3.01248L7.65078 2.34998"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.75 5.00002H8.125"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.65078 7.64999L7.42995 7.42915"
                  stroke="#F8BC30"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div className="text-[15px] font-light">Pending Approval</div>
          </div>
        )}

        {status === 2 && (
          <div className="p-1 text-[#DB1F22] gap-1 bg-white rounded-full border-[1px] border-[#DB1F22] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                className="w-[10px]"
                width="12"
                height="11"
                viewBox="0 0 12 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g id="Group">
                  <path
                    id="Path"
                    d="M1.5 1L10.5 10"
                    stroke="#DB1F22"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    id="Path_2"
                    d="M1.5 10L10.5 1"
                    stroke="#DB1F22"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
              </svg>
            </div>

            <div className="text-[15px] font-light">Canceled</div>
          </div>
        )}

        {status === 3 && (
          <div className="p-1 text-[#38A569] gap-1 bg-white rounded-full border-[1px] border-[#38A569] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.54167 9.91667L2.625 7"
                  stroke="#38A569"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.3763 4.08331L5.54297 9.91665"
                  stroke="#38A569"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div className="text-[15px] font-light">Completed</div>
          </div>
        )}

        {status === 4 && paymentConfirmed && (
          <div className="p-1 text-[#F8BC30] gap-1 bg-white rounded-full border-[1px] border-[#F8BC30] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.0013 7.34167V8.75001"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.34323 6.65833L2.35156 7.64999"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.65833 5.00002H1.25"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.35156 2.34998L3.34323 3.34164"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.0013 2.5V1.25"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.98828 3.01248L7.65078 2.34998"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75 5.00002H8.125"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.65078 7.64999L7.42995 7.42915"
                  stroke="#F8BC30"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="text-[15px] font-light">Pending approval</div>
          </div>
        )}
        {status === 4 && !paymentConfirmed && (
          <div className="p-1 text-[#DB1F22] gap-1 bg-white rounded-full border-[1px] border-[#DB1F22] flex items-center pr-[8px]">
            <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.7487 1.75V2.91667"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.08464 1.75V2.91667"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.75 5.24998H11.0833"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.0833 5.83331V4.08331C11.0833 3.11673 10.2999 2.33331 9.33333 2.33331H3.5C2.53342 2.33331 1.75 3.11673 1.75 4.08331V9.33331C1.75 10.2999 2.53342 11.0833 3.5 11.0833H5.83333"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.1445 9.32343V10.3489L10.9507 10.8407"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.207 12.8333C8.75745 12.8333 7.58203 11.6585 7.58203 10.2083C7.58203 8.78848 8.78953 7.58156 10.2094 7.58331C11.6584 7.58448 12.832 8.75931 12.832 10.2083C12.832 11.6579 11.6572 12.8333 10.207 12.8333"
                  stroke="#DB1F22"
                  strokeWidth="1.1"
                />
              </svg>
            </div>
            <div className="text-[15px] font-light">Expired</div>
          </div>
        )}
      </div>
      {!onlyImages && (
        <div className="space-y-[10px] mt-2">
          {!priceHide && (
            <div className="flex w-full justify-between items-center">
              <div className="flex items-center gap-[4px]">
                <NUSDIcon className="w-[20px]" />
                <div>
                  {pricePerDay} USDC {status !== 3 && status !== 2 ? '/night' : ''}
                </div>
              </div>
              <div className="flex items-center gap-[10px]">
                <div className="p-[6px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
                  <FavoriteIconRent />
                </div>
                <div className="text-[#959595]">{avgRate}</div>
              </div>
            </div>
          )}

          <div className="flex gap-[10px] items-center">
            <PinIcon />
            <div className="text-[#959595] w-full truncate">
              {/* {(token || tokenData)?.metaData.address.city}, {(token || tokenData)?.metaData.address.state} */}
              {(token || tokenData)?.metaData?.addressString || "/NA"}
            </div>
          </div>
          <div className="flex items-center gap-[8px] w-full ">
            <div className="flex items-center gap-[8px]">
              <div className="p-[6px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
                <GuestsIcon />
              </div>
              <div className="text-[#959595]">
                {(token || tokenData)?.metaData?.essentials?.guests}
              </div>
            </div>
            <div className="w-[1px] h-[20px] bg-[#959595]"></div>
            <div className="flex items-center gap-[8px]">
              <div className="p-[6px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
                <BathRoomIconRent />
              </div>
              <div className="text-[#959595]">
                {(token || tokenData)?.metaData?.essentials?.bathrooms}
              </div>
            </div>
            <div className="w-[1px] h-[20px] bg-[#959595]"></div>

            <div className="flex items-center gap-[8px]">
              <div className="p-[6px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
                <BedIconRent />
              </div>
              <div className="text-[#959595]">
                {(token || tokenData)?.metaData?.essentials?.beds}
              </div>
            </div>

            <div className="w-[1px] h-[20px] bg-[#959595]"></div>

            <div className="flex items-center gap-[8px]">
              <div className="p-[6px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12 7.33333H14.6667V9.99999C13.1939 9.99999 12 8.80609 12 7.33333Z"
                    stroke="#202020"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M11.3337 2C13.1746 2 14.667 3.49238 14.667 5.33333V10.6667C14.667 12.5076 13.1746 14 11.3337 14H6.00033C4.15938 14 2.66699 12.5076 2.66699 10.6667V5.33333C2.66699 3.49239 4.15938 2 6.00033 2H11.3337Z"
                    stroke="#202020"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8.66732 10.6667V14"
                    stroke="#202020"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8.67122 7.33333V2"
                    stroke="#202020"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M5.33366 7.33333H2.66699"
                    stroke="#323232"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div className="text-[#959595]">
                {(token || tokenData)?.metaData?.essentials?.square}
              </div>
              <div className="text-[#959595] text-[14px] items-top flex">
                <span>M</span>
                <span className="text-[10px] top-0">2</span>
              </div>
            </div>
          </div>
          <PurpleButton
            text={
              <div
                className="text-center text-sm"
                onClick={
                  onClick
                    ? onClick
                    : () => {
                        navigate(location.pathname + "/" + token_id);
                      }
                }
              >
                View Details
              </div>
            }
          />
        </div>
      )}
      
      {/* iCal Modal */}
      <IcalModal
        isOpen={showIcalModal}
        onClose={() => setShowIcalModal(false)}
        propertyId={token_id}
        propertyName={(token || tokenData)?.metaData?.buildingName}
      />
    </div>
  );
});

// Add display name for debugging
RentalItem.displayName = 'RentalItem';